import { Injectable } from '@angular/core';
import { CalendarStorageService } from './calendar-storage.service';
import {
  BusinessObjectService,
  CustomerService,
  ErrorHandlerServiceService,
  StaffService,
} from '../../../services';
import {
  APP_MODE,
  Appointment,
  AppointmentMessage,
  AvailableDate,
  AvailableSlots,
  BasicAppointmentSettings,
  BusinessNotification,
  BusinessObject,
  Customer,
  DateRange,
  NOTIFICATION_HISTORY_IN_DAYS,
  Order,
  Staff,
  WEEKDAY,
} from '../../../models';
import {
  BusinessObjectUtils,
  DateTimeUtils,
  NumberUtils,
} from '../../../utils';

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  constructor(
    private calendarStorageService: CalendarStorageService,
    private errorHandlerServiceService: ErrorHandlerServiceService,
    private businessObjectService: BusinessObjectService,
    private customerService: CustomerService,
    private staffService: StaffService
  ) {}

  public async getBusinessObjectById(
    businessObjectId: number
  ): Promise<BusinessObject> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    try {
      let bo: BusinessObject;

      switch (mode) {
        case APP_MODE.STAFF:
        case APP_MODE.CHECKOUT:
        case APP_MODE.CHECKIN:
        case APP_MODE.ADMIN:
          bo = await this.businessObjectService.getBusinessObjectById(
            businessObjectId
          );
          break;
        case APP_MODE.BOOKING:
          bo = await this.businessObjectService.getPublicBusinessObjectById(
            businessObjectId
          );
          break;
        default:
          bo = null;
      }

      await this.calendarStorageService.saveCachedBusinessObject(bo);

      return bo;
    } catch (e: any) {
      this.errorHandlerServiceService.handleError(e);

      if (this.isNoConnection(e)) {
        return await this.calendarStorageService.getCachedBusinessObjectById(
          businessObjectId
        );
      } else {
        throw e;
      }
    }
  }

  public quickCheckin(
    boId: number,
    customer: Customer,
    appointmentId?: number
  ): Promise<Order> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    switch (mode) {
      case APP_MODE.STAFF:
      case APP_MODE.CHECKOUT:
      case APP_MODE.CHECKIN:
      case APP_MODE.ADMIN:
        return this.businessObjectService.checkinAndCreateOrderForCustomer(
          boId,
          customer,
          appointmentId
        );
      case APP_MODE.BOOKING:
        return this.businessObjectService.checkinAndCreateOrderAsCustomer(
          boId,
          appointmentId
        );
      default:
        return Promise.resolve(null);
    }
  }

  public async getAllCustomersOfBusinessObject(
    businessObjectId: number
  ): Promise<Customer[]> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    switch (mode) {
      case APP_MODE.STAFF:
      case APP_MODE.CHECKOUT:
      case APP_MODE.CHECKIN:
      case APP_MODE.ADMIN:
        return this.customerService.getAllCustomersOfBusinessObject(
          businessObjectId
        );
      default:
        return Promise.resolve([]);
    }
  }

  public getCustomerById(customerId: number): Promise<Customer> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    if (mode === APP_MODE.BOOKING) {
      console.warn('Get customer by ID for customer is not available');
      return Promise.resolve(null);
    }

    return this.customerService.getCustomerById(customerId);
  }

  public updateCustomer(customer: Customer): Promise<boolean> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    if (mode === APP_MODE.BOOKING) {
      console.warn('Update customer for customer is not available');
      return Promise.resolve(false);
    }

    return this.customerService.updateCustomerById(customer);
  }

  public async getAllStaffsOfBusinessObject(
    businessObjectId: number
  ): Promise<Staff[]> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    try {
      let staffs: Staff[];

      switch (mode) {
        case APP_MODE.STAFF:
        case APP_MODE.CHECKOUT:
        case APP_MODE.CHECKIN:
        case APP_MODE.ADMIN:
          staffs = await this.staffService.getAllStaffsOfBusinessObject(
            businessObjectId
          );
          break;
        default:
          staffs = [];
      }

      await this.calendarStorageService.saveCachedStaffs(
        businessObjectId,
        staffs
      );

      return staffs;
    } catch (e: any) {
      this.errorHandlerServiceService.handleError(e);

      if (this.isNoConnection(e)) {
        return await this.calendarStorageService.getCachedStaffs(
          businessObjectId
        );
      } else {
        throw e;
      }
    }
  }

  public async getAllStaffsOfBoForOnlineBooking(
    boId: number
  ): Promise<Staff[]> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    try {
      let filteredStaffs: Staff[];

      switch (mode) {
        case APP_MODE.STAFF:
        case APP_MODE.CHECKOUT:
        case APP_MODE.CHECKIN:
        case APP_MODE.ADMIN: {
          const staffs = await this.getAllStaffsOfBusinessObject(boId);
          filteredStaffs = staffs?.filter((s) => s.active && s.enabled4Booking);
          break;
        }
        case APP_MODE.BOOKING:
          filteredStaffs =
            await this.staffService.getAllStaffsOfBoForOnlineBooking(boId);
          break;
        default:
          filteredStaffs = [];
      }
      await this.calendarStorageService.saveCachedStaffs(boId, filteredStaffs);

      return filteredStaffs;
    } catch (e: any) {
      this.errorHandlerServiceService.handleError(e);

      if (this.isNoConnection(e)) {
        return await this.calendarStorageService.getCachedStaffs(boId);
      } else {
        throw e;
      }
    }
  }

  public async getOnlineBookingSettingsOfBo(
    boId: number
  ): Promise<BasicAppointmentSettings> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    try {
      let settings: BasicAppointmentSettings;

      switch (mode) {
        case APP_MODE.STAFF:
        case APP_MODE.CHECKOUT:
        case APP_MODE.CHECKIN:
        case APP_MODE.ADMIN:
          settings = await this.customerService.getOnlineBookingSettingsOfBo(
            boId
          );
          break;
        case APP_MODE.BOOKING:
          settings =
            await this.customerService.getPublicOnlineBookingSettingsOfBo(boId);
          break;
        default:
          settings = null;
      }

      await this.calendarStorageService.saveCachedAppointmentSettings(
        boId,
        settings
      );

      return settings;
    } catch (e: any) {
      this.errorHandlerServiceService.handleError(e);

      if (this.isNoConnection(e)) {
        return await this.calendarStorageService.getCachedAppointmentSettings(
          boId
        );
      } else {
        throw e;
      }
    }
  }

  public async getAllBookingsForBusinessObject(
    businessObjectId: number,
    dateFrom: Date,
    dateTo: Date
  ): Promise<Appointment[]> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    let validatedDateRange: DateRange = {
      fromDate: dateFrom,
      toDate: dateTo,
    };

    if ([APP_MODE.STAFF, APP_MODE.CHECKOUT, APP_MODE.CHECKIN].includes(mode)) {
      const businessObject = await this.getBusinessObjectById(businessObjectId);

      validatedDateRange =
        BusinessObjectUtils.validateDateRangeBasingOnAppointmentLifeTime(
          businessObject,
          mode,
          {
            fromDate: dateFrom,
            toDate: dateTo,
          }
        );
    }

    if (!validatedDateRange) {
      return [];
    }

    try {
      let appointments: Appointment[];
      switch (mode) {
        case APP_MODE.STAFF:
        case APP_MODE.CHECKOUT:
        case APP_MODE.CHECKIN:
        case APP_MODE.ADMIN:
          appointments =
            await this.customerService.getAllBookingsForBusinessObject(
              businessObjectId,
              validatedDateRange.fromDate,
              validatedDateRange.toDate
            );
          break;
        default:
          appointments = [];
      }

      await this.calendarStorageService.saveCachedAppointments(appointments);

      return appointments;
    } catch (e: any) {
      this.errorHandlerServiceService.handleError(e);

      if (this.isNoConnection(e)) {
        return await this.calendarStorageService.getCachedAppointmentByRange(
          businessObjectId,
          validatedDateRange.fromDate,
          validatedDateRange.toDate
        );
      } else {
        throw e;
      }
    }
  }

  public getAllAppointmentsOfCustomer(
    boId: number,
    customerId: number
  ): Promise<Appointment[]> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    if (mode === APP_MODE.BOOKING) {
      console.warn('Get all appointments for customer is not available');
      return Promise.resolve([]);
    }

    return this.customerService.getAllAppointmentsOfCustomer(boId, customerId);
  }

  public async getAppointmentById(appointmentId: number): Promise<Appointment> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    try {
      let appointment: Appointment;

      switch (mode) {
        case APP_MODE.STAFF:
        case APP_MODE.CHECKOUT:
        case APP_MODE.CHECKIN:
        case APP_MODE.ADMIN:
          appointment = await this.customerService.getAppointmentById(
            appointmentId
          );
          break;
        case APP_MODE.BOOKING:
          appointment = await this.customerService.getAppointmentByIdAsCustomer(
            appointmentId
          );
          break;
        default:
          appointment = null;
      }

      await this.calendarStorageService.saveCachedAppointments([appointment]);

      return appointment;
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);

      if (this.isNoConnection(e)) {
        return await this.calendarStorageService.getCachedAppointmentById(
          appointmentId
        );
      } else {
        throw e;
      }
    }
  }

  public createNewAppointment(
    businessObjectId: number,
    customerId: number,
    appointment: Appointment
  ): Promise<Appointment> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    switch (mode) {
      case APP_MODE.STAFF:
      case APP_MODE.CHECKOUT:
      case APP_MODE.CHECKIN:
      case APP_MODE.ADMIN:
        return this.customerService.createNewAppointment(
          businessObjectId,
          customerId,
          appointment
        );
      case APP_MODE.BOOKING:
        return this.customerService.createAppointmentAsCustomer(
          businessObjectId,
          appointment
        );
      default:
        return Promise.resolve(null);
    }
  }

  public createNewAppointmentAsGuest(
    businessObjectId: number,
    appointment: Appointment
  ): Promise<Appointment> {
    return this.customerService.createAppointmentAsGuest(
      businessObjectId,
      appointment
    );
  }

  public async updateAppointmentById(
    appointment: Appointment
  ): Promise<boolean> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    if (mode === APP_MODE.BOOKING) {
      console.warn('Appointment update for customer is not available');
      return false;
    }

    return this.customerService.updateAppointmentById(appointment);
  }

  public cancelAppointmentByLinkId(
    linkId: string,
    message: string
  ): Promise<boolean> {
    return this.customerService.cancelAppointmentByLinkId(linkId, message);
  }

  public cancelAppointment(
    boId: number,
    appointmentId: number,
    message: string
  ): Promise<boolean> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();
    switch (mode) {
      case APP_MODE.STAFF:
      case APP_MODE.CHECKOUT:
      case APP_MODE.CHECKIN:
      case APP_MODE.ADMIN:
        return this.customerService.cancelAppointment(appointmentId, message);
      case APP_MODE.BOOKING:
        return this.customerService.cancelAppointmentAsCustomer(
          boId,
          appointmentId,
          message
        );
      default:
        return Promise.resolve(false);
    }
  }

  public deleteBlockerById(blockerId: number): Promise<boolean> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    if (mode === APP_MODE.BOOKING) {
      console.warn('Blocker deletion for customer is not available');
      return Promise.resolve(false);
    }

    return this.customerService.deleteBlockerById(blockerId);
  }

  public deleteSerieBlocker(
    blockerId: number,
    serieId: string
  ): Promise<boolean> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    if (mode === APP_MODE.BOOKING) {
      console.warn('Series blocker deletion for customer is not available');
      return Promise.resolve(false);
    }

    return this.customerService.deleteSerieBlocker(blockerId, serieId);
  }

  public writeAppointmentMessage(
    boId: number,
    appointmentId: number,
    message: string
  ): Promise<AppointmentMessage> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    switch (mode) {
      case APP_MODE.BOOKING:
        return this.customerService.writeAppointmentMessageAsCustomer(
          boId,
          appointmentId,
          message
        );
      default:
        return this.customerService.writeAppointmentMessage(
          appointmentId,
          message
        );
    }
  }

  public findAvailableSlots(
    boId: number,
    staffId: number,
    appointmentId: number,
    duration: number,
    from: string,
    to: string,
    productIds?: number[]
  ): Promise<AvailableSlots> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    switch (mode) {
      case APP_MODE.STAFF:
      case APP_MODE.CHECKOUT:
      case APP_MODE.ADMIN:
        return this.customerService.findAvailableSlots(
          boId,
          staffId,
          appointmentId,
          duration,
          from,
          to,
          productIds
        );
      case APP_MODE.CHECKIN:
      case APP_MODE.BOOKING:
        return this.customerService.findPublicAvailableSlots(
          boId,
          staffId,
          appointmentId,
          duration,
          from,
          to,
          productIds
        );
      default:
        return Promise.resolve(null);
    }
  }

  public async findAvailableDates(
    boId: number,
    staffId: number,
    fromDate: string,
    toDate: string,
    startTime?: string,
    endTime?: string,
    weekdays?: WEEKDAY[]
  ): Promise<AvailableDate[]> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    if (mode === APP_MODE.BOOKING) {
      console.warn('Finding available dates for customer is not available');
      return [];
    }

    return this.customerService.findAvailableDates(
      boId,
      staffId,
      fromDate,
      toDate,
      startTime,
      endTime,
      weekdays
    );
  }

  public async updateBusinessNotifications(
    boId: number
  ): Promise<BusinessNotification[]> {
    const mode: APP_MODE = this.calendarStorageService.getCalendarMode();

    if (
      mode !== APP_MODE.ADMIN &&
      mode !== APP_MODE.CHECKOUT &&
      mode !== APP_MODE.STAFF
    ) {
      return [];
    }

    const businessObject = await this.getBusinessObjectById(boId);

    const isAppointmentLifeTimeRestricted =
      mode !== APP_MODE.ADMIN &&
      NumberUtils.isNumber(businessObject?.appointmentLifeTimeInDays) &&
      businessObject?.appointmentLifeTimeInDays >= 0;

    const numberOfAvailableDaysBefore = isAppointmentLifeTimeRestricted
      ? Math.min(
          NOTIFICATION_HISTORY_IN_DAYS,
          businessObject?.appointmentLifeTimeInDays
        )
      : NOTIFICATION_HISTORY_IN_DAYS;

    const notificationStartDateToFetch = DateTimeUtils.addDayToDate(
      new Date(),
      -numberOfAvailableDaysBefore
    );

    const businessNotifications =
      await this.businessObjectService.fetchAllNotifications(
        businessObject.id,
        notificationStartDateToFetch
      );

    this.calendarStorageService.setBusinessNotifications(businessNotifications);

    return businessNotifications;
  }

  private isNoConnection(error: any): boolean {
    return !error.status || error.status === 504;
  }
}
